import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material'
import LanguageIcon from '@mui/icons-material/Language'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
  GET_MENU_LANGUAGES,
  SET_MENU_LANGUAGES,
  SET_TICKET_LANGUAGES,
  TRANSLATE_MENU_NAMES,
} from '../../apollo/advancedMenuMutations'
import { MENU_LANGUAGES, getLanguageLabel } from '../../config/menuLanguages'
import { mw, dialogPaperSx, pillBtnSx, aiBtnSx, menuPaperSx } from '../../views/menuWorkspaceStyles'

const NONE = ''

/**
 * Lets the owner pick a second and third menu language, choose which of them
 * customers actually see, and kick off the AI translation pass over every item
 * name and category title.
 *
 * Generating and showing are deliberately separate: an owner can build up
 * translations, review them in the Items table, and publish them only when
 * they're happy.
 */
export default function MenuLanguagesDialog({ open, onClose, restaurantId, onGenerated }) {
  const { t } = useTranslation()

  const [lang2, setLang2] = useState(NONE)
  const [lang3, setLang3] = useState(NONE)
  const [displayMode, setDisplayMode] = useState('NONE')
  // What the restaurant's printed tickets and its app show — each its own
  // setting (migration 118), also editable by the restaurant in its app.
  const [ticketModes, setTicketModes] = useState({ kitchen: 'NONE', receipt: 'NONE', screen: 'NONE' })
  const [regenerate, setRegenerate] = useState(false)
  const [error, setError] = useState('')

  const { data, loading: loadingCurrent } = useQuery(GET_MENU_LANGUAGES, {
    variables: { id: String(restaurantId) },
    skip: !open || !restaurantId,
    fetchPolicy: 'network-only',
  })

  const saved = data?.restaurant

  useEffect(() => {
    if (!saved) return
    setLang2(saved.menuLanguage2 || NONE)
    setLang3(saved.menuLanguage3 || NONE)
    setDisplayMode(saved.menuLanguageDisplay || 'NONE')
    setTicketModes({
      kitchen: saved.menuLanguageKitchenDisplay || 'NONE',
      receipt: saved.menuLanguageReceiptDisplay || 'NONE',
      screen: saved.menuLanguageScreenDisplay || 'NONE',
    })
    setRegenerate(false)
    setError('')
  }, [saved])

  const [setMenuLanguages, { loading: saving }] = useMutation(SET_MENU_LANGUAGES, {
    onError: (err) => setError(err.message),
  })
  const [setTicketLanguages, { loading: savingKitchen }] = useMutation(SET_TICKET_LANGUAGES, {
    onError: (err) => setError(err.message),
  })
  const [translateMenuNames, { loading: translating }] = useMutation(TRANSLATE_MENU_NAMES, {
    onError: (err) => setError(err.message),
  })

  const busy = saving || savingKitchen || translating

  // Switching a slot to a different language throws away what's stored there —
  // warn before the owner clicks, not after.
  const clearingSlots = useMemo(() => {
    if (!saved) return []
    const out = []
    if (saved.menuLanguage2 && (saved.menuLanguage2 !== (lang2 || null))) {
      out.push(getLanguageLabel(saved.menuLanguage2))
    }
    if (saved.menuLanguage3 && (saved.menuLanguage3 !== (lang3 || null))) {
      out.push(getLanguageLabel(saved.menuLanguage3))
    }
    return out.filter(Boolean)
  }, [saved, lang2, lang3])

  const sameLanguageTwice = !!lang2 && lang2 === lang3
  const noLanguages = !lang2 && !lang3

  const displayOptions = [
    { value: 'NONE', label: t('English only') },
    { value: 'SECOND', label: lang2 ? t('English and {{lang}}', { lang: getLanguageLabel(lang2) }) : t('English and second language'), disabled: !lang2 },
    { value: 'THIRD', label: lang3 ? t('English and {{lang}}', { lang: getLanguageLabel(lang3) }) : t('English and third language'), disabled: !lang3 },
    { value: 'BOTH', label: t('English and both'), disabled: !lang2 || !lang3 },
  ]

  // Independent of the storefront setting, so kitchen staff can see a
  // translation customers don't, or vice versa. The last three drop English:
  // a name with no translation yet still shows in English.
  const l2Label = lang2 ? getLanguageLabel(lang2) : t('second language')
  const l3Label = lang3 ? getLanguageLabel(lang3) : t('third language')
  const ticketDisplayOptions = [
    ...displayOptions,
    { value: 'SECOND_ONLY', label: t('{{lang}} only', { lang: l2Label }), disabled: !lang2 },
    { value: 'THIRD_ONLY', label: t('{{lang}} only', { lang: l3Label }), disabled: !lang3 },
    { value: 'BOTH_ONLY', label: t('{{a}} and {{b}}, no English', { a: l2Label, b: l3Label }), disabled: !lang2 || !lang3 },
  ]
  const ticketSurfaces = [
    { key: 'kitchen', label: t('Kitchen ticket') },
    { key: 'receipt', label: t('Customer receipt') },
    { key: 'screen', label: t('Restaurant app screen') },
  ]

  const persist = async () => {
    setError('')
    // Language slots first — setTicketLanguages auto-downgrades by reading
    // the restaurant's CURRENT menuLanguage2/3, so it must run after any slot
    // change in this same Save click, not in parallel with it.
    const { data: res } = await setMenuLanguages({
      variables: {
        restaurantId: String(restaurantId),
        language2: lang2 || null,
        language3: lang3 || null,
        displayMode,
      },
    })
    await setTicketLanguages({
      variables: {
        restaurantId: String(restaurantId),
        ...ticketModes,
      },
    })
    return res?.setMenuLanguages
  }

  const handleSave = async () => {
    if (sameLanguageTwice) return
    const res = await persist()
    if (res) onClose()
  }

  const handleGenerate = async () => {
    if (sameLanguageTwice || noLanguages) return

    const langNames = [lang2, lang3].filter(Boolean).map(getLanguageLabel).join(', ')
    const confirmMsg = regenerate
      ? t('Re-translate every item name, category title, modifier and size into {{langs}}? This overwrites existing translations and uses AI credits.', { langs: langNames })
      : t('Translate item names, category titles, modifiers and sizes into {{langs}}? Only names without a translation are processed. This uses AI credits.', { langs: langNames })
    if (!window.confirm(confirmMsg)) return

    // Save first so the batch reads the languages the owner just picked.
    const savedRes = await persist()
    if (!savedRes) return

    const { data: res } = await translateMenuNames({
      variables: { restaurantId: String(restaurantId), force: regenerate },
    })
    const r = res?.translateMenuNames
    if (!r) return

    if (r.queued === 0) {
      window.alert(t('Everything is already translated. Nothing to generate.'))
    } else {
      window.alert(t('Started! Translating {{n}} name(s) in the background — about {{m}} minute(s). Refresh the Items list to see them appear.', { n: r.queued, m: r.estimatedMinutes }))
      if (onGenerated) onGenerated()
      onClose()
    }
  }

  const languageSelect = (label, value, onChange, otherValue) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        MenuProps={{ PaperProps: { sx: { ...menuPaperSx, maxHeight: 360 } } }}
        sx={{ borderRadius: mw.radius.sm }}
      >
        <MenuItem value={NONE}>
          <em>{t('None')}</em>
        </MenuItem>
        {MENU_LANGUAGES.map((l) => (
          <MenuItem key={l.code} value={l.code} disabled={!!otherValue && otherValue === l.code}>
            <Box component="span" sx={{ fontWeight: 600 }}>{l.native}</Box>
            <Box component="span" sx={{ ml: 1, color: mw.color.textMuted, fontSize: '0.85em' }}>
              {l.english}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, color: mw.color.textPrimary }}>
        <LanguageIcon sx={{ color: mw.color.ai }} />
        {t('Menu Languages')}
      </DialogTitle>

      <DialogContent dividers>
        {loadingCurrent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: mw.color.textSecondary, mb: 2.5 }}>
              {t('Pick up to two more languages. AI writes the translated item and category names, and customers see them in brackets beside the English name.')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                px: 1.75,
                py: 1.25,
                borderRadius: mw.radius.sm,
                bgcolor: mw.color.subtleBg,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: mw.color.textPrimary, minWidth: 92 }}>
                {t('Primary')}
              </Typography>
              <Typography variant="body2" sx={{ color: mw.color.textSecondary }}>
                {t('English — always shown')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              {languageSelect(t('Second language'), lang2, setLang2, lang3)}
              {languageSelect(t('Third language'), lang3, setLang3, lang2)}
            </Box>

            {sameLanguageTwice && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: mw.radius.sm }}>
                {t('Pick two different languages.')}
              </Alert>
            )}

            {clearingSlots.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: mw.radius.sm }}>
                {t('Changing a language clears the names already translated into {{langs}}. Generate again to refill them.', { langs: clearingSlots.join(', ') })}
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5, fontWeight: 800, color: mw.color.textPrimary }}>
              {t('Show to customers')}
            </Typography>
            <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 1 }}>
              {t('Translations stay saved either way — this only decides what the customer website shows.')}
            </Typography>
            <RadioGroup value={displayMode} onChange={(e) => setDisplayMode(e.target.value)}>
              {displayOptions.map((o) => (
                <FormControlLabel
                  key={o.value}
                  value={o.value}
                  disabled={o.disabled}
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: mw.color.accent } }} />}
                  label={<Typography variant="body2">{o.label}</Typography>}
                />
              ))}
            </RadioGroup>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5, fontWeight: 800, color: mw.color.textPrimary }}>
              {t('Show on tickets and in the restaurant app')}
            </Typography>
            <Typography variant="caption" sx={{ color: mw.color.textMuted, display: 'block', mb: 1.5 }}>
              {t('Independent of the customer setting above, and of each other. The restaurant can also change these itself in the restaurant app, under Printers.')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {ticketSurfaces.map((s) => (
                <FormControl key={s.key} fullWidth size="small">
                  <InputLabel>{s.label}</InputLabel>
                  <Select
                    label={s.label}
                    value={ticketModes[s.key]}
                    onChange={(e) => setTicketModes((m) => ({ ...m, [s.key]: e.target.value }))}
                    MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                    sx={{ borderRadius: mw.radius.sm }}
                  >
                    {ticketDisplayOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value} disabled={o.disabled}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>

            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${mw.color.divider}` }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={regenerate}
                    onChange={(e) => setRegenerate(e.target.checked)}
                    sx={{ '&.Mui-checked': { color: mw.color.ai } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: mw.color.textSecondary }}>
                    {t('Re-translate names that already have a translation')}
                  </Typography>
                }
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: mw.radius.sm }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={busy} sx={pillBtnSx}>
          {t('Cancel')}
        </Button>
        <Button onClick={handleSave} disabled={busy || sameLanguageTwice} variant="outlined" sx={pillBtnSx}>
          {t('Save')}
        </Button>
        <Button
          onClick={handleGenerate}
          disabled={busy || noLanguages || sameLanguageTwice}
          variant="outlined"
          startIcon={translating ? <CircularProgress size={16} /> : <LanguageIcon />}
          sx={aiBtnSx}
        >
          {t('Generate Names')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
